import { create } from 'zustand'
import { authApi, usersApi, type User } from '../api/endpoints'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  setupComplete: boolean
  maskContactInfo: boolean

  checkAuth: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setup: (username: string, password: string) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setupComplete: false,
  maskContactInfo: false,

  checkAuth: async () => {
    try {
      const status = await authApi.status()
      if (status.authenticated) {
        const { data } = await usersApi.me()
        set({
          user: data,
          isAuthenticated: true,
          setupComplete: status.setup_complete,
          maskContactInfo: status.mask_contact_info ?? false,
          isLoading: false,
        })
      } else {
        set({ user: null, isAuthenticated: false, setupComplete: status.setup_complete, maskContactInfo: false, isLoading: false })
      }
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  login: async (username, password) => {
    const resp = await authApi.login(username, password)
    if (resp.otp_required) {
      // OTP required — don't set authenticated yet. Return the session ID
      // so the login page can show the OTP form.
      throw Object.assign(new Error('OTP required'), {
        otpRequired: true,
        otpSessionId: resp.otp_session_id,
      })
    }
    const { data } = await usersApi.me()
    set({ user: data, isAuthenticated: true })
  },

  logout: async () => {
    await authApi.logout()
    set({ user: null, isAuthenticated: false })
  },

  setup: async (username, password) => {
    await authApi.setup(username, password)
    const { data } = await usersApi.me()
    set({ user: data, isAuthenticated: true, setupComplete: true })
  },
}))
